import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from "react";

import { Alert, Snackbar, AlertColor } from "@mui/material";

interface NotificationContextType {
  showNotification: (message: string, type: AlertColor) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationState {
  open: boolean;
  message: string;
  type: AlertColor;
}

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notification, setNotification] = useState<NotificationState>({
    open: false,
    message: "",
    type: "info",
  });

  const showNotification = useCallback((message: string, type: AlertColor) => {
    setNotification({
      open: true,
      message,
      type,
    });
  }, []);

  const showSuccess = useCallback((message: string) => showNotification(message, "success"), [showNotification]);
  const showError = useCallback((message: string) => showNotification(message, "error"), [showNotification]);
  const showWarning = useCallback((message: string) => showNotification(message, "warning"), [showNotification]);
  const showInfo = useCallback((message: string) => showNotification(message, "info"), [showNotification]);

  const handleClose = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const value: NotificationContextType = useMemo(
    () => ({
      showNotification,
      showSuccess,
      showError,
      showWarning,
      showInfo,
    }),
    [showNotification, showSuccess, showError, showWarning, showInfo],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity={notification.type} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};
